import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import loadable from '@loadable/component';
import { ThreeDots } from 'react-loader-spinner';
import { ConfigProvider } from 'antd';

const LoadingComponent = () => (
  <div className="loader-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
    <ThreeDots color="#C86A4A" height={30} width={50} />
  </div>
);

const NotFound = loadable(() => import('pages/auth/Notfound'), {
  fallback: <LoadingComponent />,
});

const MainLayout = loadable(() => import('layout/MainLayout'), {
  fallback: <LoadingComponent />,
});

import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import Reset from './pages/auth/reset';

import ProtectedRoute from './pages/auth/ProtectedRoute';
import OnboardingGuard from './pages/auth/OnboardingGuard';
import Auth from './Auth';
import StoreProvider from './StoreProvider';
import QueryParamListener from 'QueryParamListener';
import FingerprintLoader from 'components/common/FingerprintLoader';
// import GlobalMessageCenter from 'components/common/GlobalMessageCenter';
import ErrorBoundary from 'components/common/ErrorBoundary';
import HostValidator from 'components/common/HostValidator';
import AnalyticsTracker from 'components/common/AnalyticsTracker';
import ImpersonationBanner from 'components/common/ImpersonationBanner';
import { isAllowedHost } from 'utils/hostValidation';

/******************************************
 * SAYIFY.PRO ROUTES & COMPONENTS
 ******************************************/
const SayDashboard = loadable(() => import('pages/say/dashboard/SayDashboard'), {
  fallback: <LoadingComponent />,
});
const SayFeedbackLinkList = loadable(() => import('pages/say/feedback/SayFeedbackLinkList'), {
  fallback: <LoadingComponent />,
});
const SayCreateFeedback = loadable(() => import('pages/say/feedback/SayCreateFeedback'), {
  fallback: <LoadingComponent />,
});
const PageFormDesigner = loadable(() => import('pages/say/feedback/PageFormDesigner'), {
  fallback: <LoadingComponent />,
});

// API Intakes
const ApiIntakeListPage = loadable(() => import('pages/say/api-intakes/ApiIntakeListPage'), {
  fallback: <LoadingComponent />,
});
const ApiIntakeBuilderPage = loadable(() => import('pages/say/api-intakes/ApiIntakeBuilderPage'), {
  fallback: <LoadingComponent />,
});

// Email Requests
const EmailRequestsPage = loadable(() => import('pages/say/email-requests/EmailRequestsPage'), {
  fallback: <LoadingComponent />,
});
const SayResponses = loadable(() => import('pages/say/responses/SayResponses'), {
  fallback: <LoadingComponent />,
});
const ResponseInbox = loadable(() => import('pages/say/responses/ResponseInbox'), {
  fallback: <LoadingComponent />,
});
const RepliesPage = loadable(() => import('pages/say/inbox/RepliesPage'), {
  fallback: <LoadingComponent />,
});
const ResponseDetailPage = loadable(() => import('pages/say/responses/ResponseDetailPage'), {
  fallback: <LoadingComponent />,
});
const SayWebhooks = loadable(() => import('pages/say/webhooks/SayWebhooks'), {
  fallback: <LoadingComponent />,
});
const IntegrationsHubPage = loadable(() => import('pages/integrations/IntegrationsHubPage'), {
  fallback: <LoadingComponent />,
});
const SaySettings = loadable(() => import('pages/say/settings/SaySettings'), {
  fallback: <LoadingComponent />,
});
const BrandingPage = loadable(() => import('pages/say/settings/BrandingPage'), {
  fallback: <LoadingComponent />,
});
const SayApiManagement = loadable(() => import('pages/say/api-docs/SayApiManagement'), {
  fallback: <LoadingComponent />,
});
const AgentConnectorPage = loadable(() => import('pages/say/agent-connector/AgentConnectorPage'), {
  fallback: <LoadingComponent />,
});
const SayPublicFeedback = loadable(() => import('pages/say/public/SayPublicFeedback'), {
  fallback: <LoadingComponent />,
});
const WorkspaceKnowledgePage = loadable(() => import('pages/say/knowledge/WorkspaceKnowledgePage'), {
  fallback: <LoadingComponent />,
});
const PublicIntakePage = loadable(() => import('pages/public/PublicIntakePage'), {
  fallback: <LoadingComponent />,
});
const TenantCollectorPage = loadable(() => import('pages/public/TenantCollectorPage'), {
  fallback: <LoadingComponent />,
});
const PublicThreadPage = loadable(() => import('pages/public/PublicThreadPage'), {
  fallback: <LoadingComponent />,
});
const AcceptInvitation = loadable(() => import('pages/workspace/AcceptInvitation'), {
  fallback: <LoadingComponent />,
});
const OnboardingWizard = loadable(() => import('pages/onboarding/OnboardingWizard'), {
  fallback: <LoadingComponent />,
});

// First-run flow: pick a form, answer it yourself, see the case.
const StartFlow = loadable(() => import('pages/onboarding/StartFlow'), {
  fallback: <LoadingComponent />,
});

// Support Ticketing System
const TicketList = loadable(() => import('pages/Support/TicketList'), {
  fallback: <LoadingComponent />,
});
const CreateTicket = loadable(() => import('pages/Support/CreateTicket'), {
  fallback: <LoadingComponent />,
});
const TicketDetail = loadable(() => import('pages/Support/TicketDetail'), {
  fallback: <LoadingComponent />,
});
const AdminTicketDashboard = loadable(() => import('pages/Support/AdminTicketDashboard'), {
  fallback: <LoadingComponent />,
});
const AdminTicketDetail = loadable(() => import('pages/Support/AdminTicketDetail'), {
  fallback: <LoadingComponent />,
});

// Product Feedback & Roadmap
const ProdFeedbackListPage = loadable(() => import('pages/prod-feedback/ProdFeedbackListPage'), {
  fallback: <LoadingComponent />,
});
const ProdFeedbackCreatePage = loadable(() => import('pages/prod-feedback/ProdFeedbackCreatePage'), {
  fallback: <LoadingComponent />,
});
const ProdFeedbackDetailPage = loadable(() => import('pages/prod-feedback/ProdFeedbackDetailPage'), {
  fallback: <LoadingComponent />,
});
const AdminProdKanbanPage = loadable(() => import('pages/prod-feedback/AdminProdKanbanPage'), {
  fallback: <LoadingComponent />,
});
const AdminProdFeedbackDetailPage = loadable(() => import('pages/prod-feedback/AdminProdFeedbackDetailPage'), {
  fallback: <LoadingComponent />,
});

// Blog Admin
const BlogPostList = loadable(() => import('pages/say/blog-admin/BlogPostList'), {
  fallback: <LoadingComponent />,
});
const BlogPostForm = loadable(() => import('pages/say/blog-admin/BlogPostForm'), {
  fallback: <LoadingComponent />,
});

// Referrals
const ReferralDashboard = loadable(() => import('pages/referral/ReferralDashboard'), {
  fallback: <LoadingComponent />,
});
const AdminReferralDashboard = loadable(() => import('pages/referral/admin/AdminReferralDashboard'), {
  fallback: <LoadingComponent />,
});

// Billing
const BillingPage = loadable(() => import('pages/say/billing/BillingPage'), {
  fallback: <LoadingComponent />,
});
const CreditHistoryPage = loadable(() => import('pages/say/billing/CreditHistoryPage'), {
  fallback: <LoadingComponent />,
});

// Addons
const AddonsPage = loadable(() => import('pages/say/addons/AddonsPage'), {
  fallback: <LoadingComponent />,
});

// AppSumo Redeem
const RedeemAppSumo = loadable(() => import('pages/plans/RedeemAppSumo'), {
  fallback: <LoadingComponent />,
});

// Email Marketing Admin - Unified Dashboard
const EmailMarketingDashboard = loadable(() => import('pages/admin-email/EmailMarketingDashboard'), {
  fallback: <LoadingComponent />,
});

// Enterprise Admin
const EnterpriseAdminPage = loadable(() => import('pages/say/settings/EnterpriseAdminPage'), {
  fallback: <LoadingComponent />,
});

// AppSumo Admin
const AppSumoAdminPage = loadable(() => import('pages/admin/AppSumoAdminPage'), {
  fallback: <LoadingComponent />,
});

// Billing Admin
const BillingAdminPage = loadable(() => import('pages/admin/BillingAdminPage'), {
  fallback: <LoadingComponent />,
});
const AdminIntelligencePage = loadable(() => import('pages/admin/AdminIntelligencePage'), {
  fallback: <LoadingComponent />,
});
const UserAdminPage = loadable(() => import('pages/admin/UserAdminPage'), {
  fallback: <LoadingComponent />,
});
const AdminHomePage = loadable(() => import('pages/admin/AdminHomePage'), {
  fallback: <LoadingComponent />,
});
const SignupFunnelAdminPage = loadable(() => import('pages/admin/SignupFunnelAdminPage'), {
  fallback: <LoadingComponent />,
});

// Templates + Inbox + Alerts + Marketing
const TemplatesPage = loadable(() => import('pages/say/templates/TemplatesPage'), {
  fallback: <LoadingComponent />,
});
const WorkflowsPage = loadable(() => import('pages/say/marketing/WorkflowsPage'), {
  fallback: <LoadingComponent />,
});
const WorkflowBuilder = loadable(() => import('pages/say/marketing/WorkflowBuilder'), {
  fallback: <LoadingComponent />,
});
const EmailTemplatesPage = loadable(() => import('pages/say/marketing/EmailTemplatesPage'), {
  fallback: <LoadingComponent />,
});
const EmailBuilderPage = loadable(() => import('pages/say/marketing/EmailBuilderPage'), {
  fallback: <LoadingComponent />,
});
const EmailLogsPage = loadable(() => import('pages/say/marketing/EmailLogsPage'), {
  fallback: <LoadingComponent />,
});
const InboxPage = loadable(() => import('pages/say/inbox/InboxPage'), {
  fallback: <LoadingComponent />,
});
const CaseDetailPage = loadable(() => import('pages/say/inbox/CaseDetailPage'), {
  fallback: <LoadingComponent />,
});
const AlertsPage = loadable(() => import('pages/say/alerts/AlertsPage'), {
  fallback: <LoadingComponent />,
});
const ContactsPage = loadable(() => import('pages/say/contacts/ContactsPage'), {
  fallback: <LoadingComponent />,
});
const ContactDetailPage = loadable(() => import('pages/say/contacts/ContactDetailPage'), {
  fallback: <LoadingComponent />,
});
const SayAnalytics = loadable(() => import('pages/say/analytics/SayAnalytics'), {
  fallback: <LoadingComponent />,
});

const PDFTemplatesPage = loadable(() => import('pages/say/pdf/PDFTemplatesPage'), {
  fallback: <LoadingComponent />,
});

// Reports
const SayReports = loadable(() => import('pages/say/reports/SayReports'), {
  fallback: <LoadingComponent />,
});
const SayFormReport = loadable(() => import('pages/say/reports/SayFormReport'), {
  fallback: <LoadingComponent />,
});

// Widget Playground
const WidgetPlayground = loadable(() => import('pages/say/playground/WidgetPlayground'), {
  fallback: <LoadingComponent />,
});

// Task Board (Kanban)
const TaskBoardPage = loadable(() => import('pages/say/tasks/TaskBoardPage'), {
  fallback: <LoadingComponent />,
});

// Destinations (Phase 2 Review Tables)
const DestinationsPage = loadable(() => import('pages/say/destinations/DestinationsPage'), {
  fallback: <LoadingComponent />,
});

// Error Pages
const ServerError = loadable(() => import('pages/auth/ServerError'), {
  fallback: <LoadingComponent />,
});
const BadRequest = loadable(() => import('pages/auth/BadRequest'), {
  fallback: <LoadingComponent />,
});

const RootRoute = () => {
  const isCustomDomainRoot = window.location.pathname === '/' && !isAllowedHost(window.location.hostname);

  if (isCustomDomainRoot) {
    return <TenantCollectorPage />;
  }

  return (
    <HostValidator>
      <ProtectedRoute>
        <OnboardingGuard>
          <MainLayout />
        </OnboardingGuard>
      </ProtectedRoute>
    </HostValidator>
  );
};


const App = () => {

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#C86A4A', colorLink: '#C86A4A', colorInfo: '#C86A4A' } }}>
      <StoreProvider>
        <FingerprintLoader />
        {/* <GlobalMessageCenter /> */}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AnalyticsTracker />
          <QueryParamListener />
          <ImpersonationBanner />
          <ErrorBoundary>
            <Routes>
              {/* Authentication Routes - Restricted to localhost and app.sayify.pro */}
              <Route path="/login" element={<HostValidator><Auth /></HostValidator>}>
                <Route path="" element={<Login />} />
              </Route>
              <Route path="/signup" element={<HostValidator><SignUp /></HostValidator>} />
              <Route path="/reset-password" element={<HostValidator><Reset /></HostValidator>} />

              {/* First-run flow - Restricted to localhost and app.sayify.pro */}
              <Route path="/start" element={<HostValidator><ProtectedRoute><StartFlow /></ProtectedRoute></HostValidator>} />
              <Route path="/start/:step" element={<HostValidator><ProtectedRoute><StartFlow /></ProtectedRoute></HostValidator>} />

              {/* Onboarding Route - superseded by /start, kept so old links and
                  bookmarks land in the current flow instead of 404ing. */}
              <Route path="/onboarding" element={<Navigate to="/start" replace />} />
              <Route path="/onboarding/templates" element={<Navigate to="/start" replace />} />

              {/* 
              Protected Routes - Wrapped with HostValidator to enforce host restrictions
              Only accessible on localhost or app.sayify.pro 
            */}
              <Route path="/" element={<RootRoute />}>

                {/* Redirect root to Dashboard */}
                <Route index element={<SayDashboard />} />

                {/* SAYIFY.PRO ROUTES - Root Level */}
                <Route path="dashboard" element={<SayDashboard />} />
                <Route path="forms" element={<SayFeedbackLinkList />} />
                <Route path="forms/create" element={<SayCreateFeedback />} />
                <Route path="forms/edit/:linkUuid" element={<SayCreateFeedback />} />
                <Route path="forms/designer/:linkUuid" element={<PageFormDesigner />} />
                {/* Backwards-compatible aliases */}
                <Route path="links" element={<SayFeedbackLinkList />} />
                <Route path="links/create" element={<SayCreateFeedback />} />
                <Route path="links/edit/:linkUuid" element={<SayCreateFeedback />} />
                <Route path="links/designer/:linkUuid" element={<PageFormDesigner />} />
                {/* Legacy voice-forms redirects */}
                <Route path="voice-forms" element={<Navigate to="/forms" replace />} />
                <Route path="voice-forms/*" element={<Navigate to="/forms" replace />} />

                {/* API Intakes Routes */}
                <Route path="api-intakes" element={<ApiIntakeListPage />} />
                <Route path="api-intakes/create" element={<ApiIntakeBuilderPage />} />
                <Route path="api-intakes/edit/:linkUuid" element={<ApiIntakeBuilderPage />} />

                {/* Email Requests */}
                <Route path="email-requests" element={<EmailRequestsPage />} />

                {/* Inbox Routes */}
                <Route path="inbox" element={<ResponseInbox />} />
                <Route path="inbox/:responseId" element={<ResponseInbox />} />
                <Route path="links/:linkId/inbox" element={<ResponseInbox />} />
                <Route path="replies" element={<RepliesPage />} />
                <Route path="replies/:replyUuid" element={<RepliesPage />} />
                <Route path="webhooks" element={<SayWebhooks />} />
                <Route path="integrations" element={<IntegrationsHubPage />} />
                <Route path="settings" element={<SaySettings />} />
                <Route path="branding" element={<BrandingPage />} />
                <Route path="api-management" element={<SayApiManagement />} />
                <Route path="agent-connector" element={<AgentConnectorPage />} />
                <Route path="knowledge" element={<WorkspaceKnowledgePage />} />

                {/* Support Ticketing Routes */}
                <Route path="support" element={<TicketList />} />
                <Route path="support/create" element={<CreateTicket />} />
                <Route path="support/tickets/:ticketUuid" element={<TicketDetail />} />
                <Route path="support/admin" element={<AdminTicketDashboard />} />
                <Route path="support/admin/tickets/:ticketUuid" element={<AdminTicketDetail />} />

                {/* Product Feedback & Roadmap Routes */}
                <Route path="product-feedback" element={<ProdFeedbackListPage />} />
                <Route path="product-feedback/create" element={<ProdFeedbackCreatePage />} />
                <Route path="product-feedback/admin/:uuid" element={<AdminProdFeedbackDetailPage />} />
                <Route path="product-feedback/:uuid" element={<ProdFeedbackDetailPage />} />
                <Route path="admin/product-feedback" element={<AdminProdKanbanPage />} />
                <Route path="admin/product-feedback/:uuid" element={<AdminProdFeedbackDetailPage />} />

                {/* Blog Admin Routes */}
                <Route path="admin/blog" element={<BlogPostList />} />
                <Route path="admin/blog/new" element={<BlogPostForm />} />
                <Route path="admin/blog/edit/:id" element={<BlogPostForm />} />

                {/* Referrals Route */}
                <Route path="referrals" element={<ReferralDashboard />} />
                <Route path="admin/referrals" element={<AdminReferralDashboard />} />

                {/* Billing Route */}
                <Route path="billing" element={<BillingPage />} />
                <Route path="credit-history" element={<CreditHistoryPage />} />
                <Route path="addons" element={<AddonsPage />} />

                {/* AppSumo LTD Redeem */}
                <Route path="redeem-appsumo" element={<RedeemAppSumo />} />

                {/* Email Marketing Admin - Unified Dashboard */}
                <Route path="admin/email-marketing" element={<EmailMarketingDashboard />} />

                {/* Enterprise Admin */}
                <Route path="admin/enterprise" element={<EnterpriseAdminPage />} />

                {/* AppSumo Admin */}
                <Route path="admin/appsumo" element={<AppSumoAdminPage />} />

                {/* Billing Admin */}
                <Route path="admin/billing" element={<BillingAdminPage />} />
                <Route path="admin/intelligence" element={<AdminIntelligencePage />} />
                <Route path="admin/users" element={<UserAdminPage />} />

                {/* Admin Home + Signup Funnel */}
                <Route path="admin" element={<AdminHomePage />} />
                <Route path="admin/signup-funnel" element={<SignupFunnelAdminPage />} />

                {/* Public Route - kept distinct but could vary */}
                <Route path="p/preview" element={<SayPublicFeedback />} />

                {/* Templates, Actions & Alerts Routes */}
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="actions" element={<InboxPage />} />
                <Route path="actions/:caseUuid" element={<InboxPage />} />
                <Route path="tasks" element={<TaskBoardPage />} />
                <Route path="destinations" element={<DestinationsPage />} />
                <Route path="destinations/:destinationUuid" element={<DestinationsPage />} />
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="alerts/:alertUuid" element={<AlertsPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="contacts/:contactUuid" element={<ContactDetailPage />} />
                <Route path="workflows" element={<WorkflowsPage />} />
                <Route path="workflows/builder/:id" element={<WorkflowBuilder />} />
                <Route path="email-templates" element={<EmailTemplatesPage />} />
                <Route path="email-templates/builder/:id" element={<EmailBuilderPage />} />
                <Route path="email-logs" element={<EmailLogsPage />} />
                <Route path="pdf-templates" element={<PDFTemplatesPage />} />
                <Route path="pdf-templates/:uuid" element={<PDFTemplatesPage />} />

                {/* Analytics Route */}
                <Route path="analytics" element={<SayAnalytics />} />

                {/* Reports Routes */}
                <Route path="reports" element={<SayReports />} />
                <Route path="reports/:formUuid" element={<SayFormReport />} />
              </Route>

              {/* 
              PUBLIC INTAKE PAGE - Standalone (No MainLayout, No Host Validation)
              This is the ONLY route accessible on any subdomain or custom domain
            */}
              {/* Widget Playground — standalone layout, authenticated */}
              <Route path="/widget-playground" element={<HostValidator><ProtectedRoute><WidgetPlayground /></ProtectedRoute></HostValidator>} />

              <Route path="/i/:slug/preview" element={<PublicIntakePage />} />
              <Route path="/i/:slug/thread/:token" element={<PublicThreadPage />} />
              <Route path="/i/:slug" element={<PublicIntakePage />} />

              {/* Invitation Acceptance - Restricted to localhost and app.sayify.pro */}
              <Route path="/accept-invitation/:token" element={<HostValidator><AcceptInvitation /></HostValidator>} />

              {/* Error Pages - Restricted to localhost and app.sayify.pro */}
              <Route path="/error/500" element={<HostValidator><ServerError /></HostValidator>} />
              <Route path="/error/400" element={<HostValidator><BadRequest /></HostValidator>} />

              <Route path="*" element={<HostValidator><NotFound /></HostValidator>} />
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </StoreProvider>
    </ConfigProvider>
  );
};
export default App;
